import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  EnrichedEntryStatus
} from '../../types/entry.types';
const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v === 0) return '0.000';
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const fmtQty = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v <= 0) return '—';
  if (v >= 1_000_000) return `${Math.floor(v / 100_000) / 10}M`;
  if (v >= 1_000) return `${Math.floor(v / 100) / 10}k`;
  if (v >= 100) return String(Math.floor(v));
  return String(v);
};

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.2,
  px: 0.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

export interface CreateEntrySumRuleParams {
  pair: ManualConfirmedPair;
  platform: 'kalshi' | 'polymarket';
  thresholdSum: number;
  size: number;
  maxVolumeUsd: number;
}

interface Props {
  pairs: ManualConfirmedPair[];
  /** Rules of the `sum_of_sides` type — drives row controls. */
  rules: EntryRule[] | undefined;
  /** All rules (any type) keyed by pairId — used for status indicator. */
  anyRulesByPairId: Map<string, EntryRule>;
  getState: (id: string) => EnrichedEntryStatus | undefined;
  onCreateRule: (params: CreateEntrySumRuleParams) => void;
  onTogglePlatform: (
    rule: EntryRule,
    platform: 'kalshi' | 'polymarket',
    enabled: boolean
  ) => void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
}

const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

type SortKey = 'name' | 'sum' | 'delta';
type SortDir = 'asc' | 'desc';

export const EntryTabSumOfSides = ({
  pairs,
  rules,
  anyRulesByPairId,
  getState,
  onCreateRule,
  onTogglePlatform,
  onUpdateRule
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('delta');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const rulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      map.set(rule.pairId, rule);
    }
    return map;
  }, [rules]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        setSortDir(key === 'delta' ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  // Compute live sum + delta per pair from current sockets/state. Sort
  // direction defaults to "ascending delta first" — i.e. pairs that are
  // *furthest below* their threshold (best entry candidates) bubble to
  // the top.
  const sortedPairs = useMemo(() => {
    const annotated = pairs.map(pair => {
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      const liveState =
        (pair._id ? getState(pair._id) : undefined) ??
        (rule ? getState(rule._id) : undefined);
      const pAsk = liveState?.polyAsk?.price ?? null;
      const kAsk = liveState?.kalshiAsk?.price ?? null;
      // Apply taker fees on the ASK side: when entering as a TAKER on both
      // legs we pay raw asks plus taker fees. We approximate that with the
      // same fee model the unwinder uses, but applied to the BUY direction.
      const grossSum = pAsk != null && kAsk != null ? pAsk + kAsk : null;
      const threshold = rule?.thresholdSum ?? DEFAULT_THRESHOLD;
      const delta = grossSum != null ? threshold - grossSum : null;

      const hasSim = grossSum != null;
      const metric =
        sortKey === 'sum' ? grossSum : sortKey === 'delta' ? delta : null;
      return { pair, metric, hasSim, grossSum };
    });
    annotated.sort((a, b) => {
      if (sortKey === 'name') {
        const an = a.pair.pairName ?? '';
        const bn = b.pair.pairName ?? '';
        return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      }
      // Pairs with no live sim sink to the bottom regardless of direction.
      if (a.hasSim !== b.hasSim) return a.hasSim ? -1 : 1;
      if (a.metric == null && b.metric == null) return 0;
      if (a.metric == null) return 1;
      if (b.metric == null) return -1;
      return sortDir === 'desc' ? b.metric - a.metric : a.metric - b.metric;
    });
    return annotated.map(x => x.pair);
  }, [pairs, rulesByPairId, getState, sortKey, sortDir]);

  if (pairs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No manually confirmed pairs found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' }
  } as const;

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ ...gSx, width: 200, top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
            >
              <TableSortLabel
                active={sortKey === 'name'}
                direction={sortKey === 'name' ? sortDir : 'asc'}
                onClick={() => handleSortClick('name')}
                sx={sortLabelSx}
              >
                Market
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={4}>
              Market Data
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={3}>
              Parameters
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={2}>
              Live Sums (ASK)
            </TableCell>
            <TableCell sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }} colSpan={2}>
              Ctrl
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">P.Ask</TableCell>
            <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 32, top: 22, backgroundColor: '#1a1a2e' }} align="right">K.Ask</TableCell>
            <TableCell sx={{ ...hSx, width: 24, top: 22, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, width: 40, top: 22, backgroundColor: '#1a1a2e' }} align="right">Thr</TableCell>
            <TableCell sx={{ ...hSx, width: 30, top: 22, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
            <TableCell sx={{ ...hSx, width: 36, top: 22, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Sum of asks (P.Ask + K.Ask). Click to sort."
              onClick={() => handleSortClick('sum')}
            >
              <TableSortLabel
                active={sortKey === 'sum'}
                direction={sortKey === 'sum' ? sortDir : 'desc'}
                sx={sortLabelSx}
              >
                Sum
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...hSx, width: 44, top: 22, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
              align="right"
              title="Threshold − Sum (positive = below threshold = good entry). Click to sort."
              onClick={() => handleSortClick('delta')}
            >
              <TableSortLabel
                active={sortKey === 'delta'}
                direction={sortKey === 'delta' ? sortDir : 'asc'}
                sx={sortLabelSx}
              >
                Δ
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">P</TableCell>
            <TableCell sx={{ ...hSx, width: 28, top: 22, backgroundColor: '#1a1a2e' }} align="center">K</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPairs.map(pair => {
            const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
            const anyRule = pair._id ? anyRulesByPairId.get(pair._id) : undefined;
            const liveState =
              (pair._id ? getState(pair._id) : undefined) ??
              (rule ? getState(rule._id) : undefined);
            return (
              <SumRow
                key={pair._id ?? pair.pairName}
                pair={pair}
                rule={rule}
                anyRule={anyRule}
                liveState={liveState}
                onCreateRule={onCreateRule}
                onTogglePlatform={onTogglePlatform}
                onUpdateRule={onUpdateRule}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface RowProps {
  pair: ManualConfirmedPair;
  rule?: EntryRule;
  anyRule?: EntryRule;
  liveState?: EnrichedEntryStatus;
  onCreateRule: (params: CreateEntrySumRuleParams) => void;
  onTogglePlatform: (
    rule: EntryRule,
    platform: 'kalshi' | 'polymarket',
    enabled: boolean
  ) => void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
}

const SumRow = memo(({
  pair,
  rule,
  anyRule,
  liveState,
  onCreateRule,
  onTogglePlatform,
  onUpdateRule
}: RowProps) => {
  const ruleSize =
    rule?.kalshiConfig?.contractsToBuy ??
    rule?.polymarketConfig?.contractsToBuy ??
    DEFAULT_SIZE;
  const initThreshold =
    rule?.thresholdSum != null ? String(rule.thresholdSum) : String(DEFAULT_THRESHOLD);
  const initSize = String(ruleSize);
  const initMaxVol = String(rule?.maxVolumeUsd ?? DEFAULT_MAX_VOL);

  const [threshold, setThreshold] = useState(initThreshold);
  const [size, setSize] = useState(initSize);
  const [maxVol, setMaxVol] = useState(initMaxVol);
  const [savedThreshold, setSavedThreshold] = useState(initThreshold);
  const [savedSize, setSavedSize] = useState(initSize);
  const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingPoly, setTogglingPoly] = useState(false);
  const [togglingKalshi, setTogglingKalshi] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    platform: 'kalshi' | 'polymarket';
    enabled: boolean;
    /** Snapshot of pAsk + kAsk at the time the dialog opened, used to
     *  detect price drift before the operator clicks Activate. */
    snappedPAsk: number | null;
    snappedKAsk: number | null;
    snappedSum: number | null;
  } | null>(null);

  // Keep inputs in sync with rule changes only when user is not mid-edit.
  useEffect(() => {
    if (!rule) return;
    const dirty =
      threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;
    if (dirty) return;
    if (rule.thresholdSum != null) {
      const next = String(rule.thresholdSum);
      if (next !== savedThreshold) {
        setThreshold(next);
        setSavedThreshold(next);
      }
    }
    const ruleSizeNow =
      rule.kalshiConfig?.contractsToBuy ?? rule.polymarketConfig?.contractsToBuy;
    if (ruleSizeNow != null) {
      const next = String(ruleSizeNow);
      if (next !== savedSize) {
        setSize(next);
        setSavedSize(next);
      }
    }
    const nextMax = String(rule.maxVolumeUsd);
    if (nextMax !== savedMaxVol) {
      setMaxVol(nextMax);
      setSavedMaxVol(nextMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rule?.thresholdSum,
    rule?.maxVolumeUsd,
    rule?.kalshiConfig?.contractsToBuy,
    rule?.polymarketConfig?.contractsToBuy
  ]);

  const isDirty =
    threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;

  const validateThreshold = useCallback(
    (value: string): string | null => {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n <= 0) return 'Must be > 0';
      if (n >= 1) return 'Must be < 1.00 (hard cap)';
      return null;
    },
    []
  );

  // Live error message updates as the user types.
  useEffect(() => {
    setThresholdError(validateThreshold(threshold));
  }, [threshold, validateThreshold]);

  const handleSave = useCallback(async () => {
    if (!isDirty || !rule) return;
    const tErr = validateThreshold(threshold);
    if (tErr) {
      setThresholdError(tErr);
      return;
    }
    const parsedThreshold = Number.parseFloat(threshold);
    const parsedSize = Number.parseInt(size, 10);
    const parsedMaxVol = Number.parseFloat(maxVol);
    const dto: UpdateEntryRuleDto = {};
    if (
      Number.isFinite(parsedThreshold) &&
      parsedThreshold > 0 &&
      parsedThreshold < 1 &&
      threshold !== savedThreshold
    ) {
      dto.thresholdSum = parsedThreshold;
    }
    if (Number.isFinite(parsedMaxVol) && parsedMaxVol > 0 && maxVol !== savedMaxVol) {
      dto.maxVolumeUsd = parsedMaxVol;
    }
    if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
      if (rule.kalshiConfig) {
        dto.kalshiConfig = {
          side: rule.kalshiConfig.side,
          ticker: rule.kalshiConfig.ticker,
          contractsToBuy: parsedSize
        };
      }
      if (rule.polymarketConfig) {
        dto.polymarketConfig = {
          side: rule.polymarketConfig.side,
          tokenId: rule.polymarketConfig.tokenId,
          conditionId: rule.polymarketConfig.conditionId,
          contractsToBuy: parsedSize
        };
      }
    }
    setSaving(true);
    const ok = await onUpdateRule(rule, dto);
    if (ok) {
      setSavedThreshold(threshold);
      setSavedSize(size);
      setSavedMaxVol(maxVol);
    }
    setSaving(false);
  }, [
    isDirty,
    threshold,
    size,
    maxVol,
    savedThreshold,
    savedSize,
    savedMaxVol,
    rule,
    onUpdateRule,
    validateThreshold
  ]);

  // Top-of-book asks (the only side that matters for entry).
  const pAsk = liveState?.polyAsk?.price ?? null;
  const kAsk = liveState?.kalshiAsk?.price ?? null;
  const pAskSize = liveState?.polyAsk?.size;
  const kAskSize = liveState?.kalshiAsk?.size;

  const grossSum = pAsk != null && kAsk != null ? pAsk + kAsk : null;
  const thresholdNum = Number.parseFloat(threshold);
  const thresholdValid = Number.isFinite(thresholdNum) && thresholdNum > 0 && thresholdNum < 1;
  // Δ for entry direction = threshold − currentSumOfAsks. Positive = below
  // threshold = ready to fire.
  const delta = grossSum != null && thresholdValid ? thresholdNum - grossSum : null;
  const meetsThreshold = delta != null && delta >= 0;
  const overHardCap = grossSum != null && grossSum >= ENTRY_HARD_CAP_GROSS;

  const inSx = {
    '& .MuiInputBase-input': {
      fontSize: '0.62rem',
      py: 0.15,
      px: 0.2,
      textAlign: 'right',
      width: 28
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.08)'
    },
    minWidth: 0
  } as const;

  const tooltipTitle = (
    <Box>
      <Typography sx={{ fontSize: '0.7rem' }}>
        {pair.polymarketTitle || pair.pairName}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
        {pair.kalshiTitle || pair.pairName}
      </Typography>
    </Box>
  );

  // Three-layer hard-cap guard:
  //   1. threshold input rejects >= 1.0 (already enforced by validator)
  //   2. row outline is red when grossSum >= 0.995
  //   3. confirmation dialog blocks Activate when grossSum >= 0.995
  const rowSx = overHardCap
    ? {
        '&:hover': { backgroundColor: 'rgba(244,67,54,0.05)' },
        outline: '1px solid rgba(244,67,54,0.5)'
      }
    : { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } };

  const doToggle = useCallback(
    (platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      const setLoading = platform === 'kalshi' ? setTogglingKalshi : setTogglingPoly;
      setLoading(true);
      if (rule) {
        onTogglePlatform(rule, platform, enabled);
      } else if (enabled) {
        const tErr = validateThreshold(threshold);
        if (tErr) {
          setThresholdError(tErr);
          setLoading(false);
          return;
        }
        const parsedThr = Number.parseFloat(threshold);
        const parsedSize = Number.parseInt(size, 10);
        const parsedMaxV = Number.parseFloat(maxVol);
        onCreateRule({
          pair,
          platform,
          thresholdSum:
            Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
              ? parsedThr
              : DEFAULT_THRESHOLD,
          size: Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : DEFAULT_SIZE,
          maxVolumeUsd:
            Number.isFinite(parsedMaxV) && parsedMaxV > 0 ? parsedMaxV : DEFAULT_MAX_VOL
        });
      }
      setTimeout(() => setLoading(false), 2000);
    },
    [rule, threshold, size, maxVol, pair, onCreateRule, onTogglePlatform, validateThreshold]
  );

  const handleToggle = useCallback(
    (platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      if (enabled) {
        setConfirmDialog({
          platform,
          enabled,
          snappedPAsk: pAsk,
          snappedKAsk: kAsk,
          snappedSum: grossSum
        });
        return;
      }
      doToggle(platform, false);
    },
    [doToggle, pAsk, kAsk, grossSum]
  );

  const handleConfirmToggle = useCallback(() => {
    if (!confirmDialog) return;
    // Re-read the latest socket state. The hard-cap guard reads from the
    // CURRENT grossSum (closure already captures live values), not the
    // snapped one — never let a stale snapshot bypass the cap.
    if (overHardCap) return;
    doToggle(confirmDialog.platform, confirmDialog.enabled);
    setConfirmDialog(null);
  }, [confirmDialog, doToggle, overHardCap]);

  // "Re-confirm": if any of the four prices changed > 1 tick (0.01)
  // since the dialog opened, force the operator to acknowledge the new
  // prices. We compare just pAsk / kAsk because those are the ones that
  // matter for entry; bid drift doesn't change the activation calculus.
  const TICK = 0.01;
  const priceDriftedSinceOpen = useMemo(() => {
    if (!confirmDialog) return false;
    const dPa =
      confirmDialog.snappedPAsk != null && pAsk != null
        ? Math.abs(pAsk - confirmDialog.snappedPAsk)
        : 0;
    const dKa =
      confirmDialog.snappedKAsk != null && kAsk != null
        ? Math.abs(kAsk - confirmDialog.snappedKAsk)
        : 0;
    return dPa > TICK || dKa > TICK;
  }, [confirmDialog, pAsk, kAsk]);

  const handleReconfirm = useCallback(() => {
    if (!confirmDialog) return;
    setConfirmDialog({
      ...confirmDialog,
      snappedPAsk: pAsk,
      snappedKAsk: kAsk,
      snappedSum: grossSum
    });
  }, [confirmDialog, pAsk, kAsk, grossSum]);

  const ruleStatusColor =
    anyRule?.status === 'active'
      ? '#4caf50'
      : anyRule?.status === 'paused'
      ? '#ff9800'
      : 'grey.600';

  return (
    <>
    <TableRow sx={rowSx}>
      {/* Market */}
      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
          {/* Rule status indicator: green=active, orange=paused,
              dim=none. Mirrors the unwinder's debug icon position so
              the layout is consistent. */}
          <Tooltip
            title={
              anyRule
                ? `Entry rule: ${anyRule.status}`
                : 'No rule yet — toggle a platform to create'
            }
            placement="top"
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: ruleStatusColor,
                mt: 0.5,
                ml: 0.2,
                flexShrink: 0
              }}
            />
          </Tooltip>
          <Tooltip title={tooltipTitle} placement="top-start">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <a
                  href={pair.polymarket?.marketUrl ?? liveState?.polymarketMarketUrl ?? undefined}
                  target="_blank"
                  rel="noopener"
                  onClick={e => e.stopPropagation()}
                >
                  <img
                    src="/assets/exchanges/png/100x100/polymarket.png"
                    alt="P"
                    style={{ width: 13, height: 13, borderRadius: '50%' }}
                  />
                </a>
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {pair.polymarketTitle || pair.pairName}
                </Typography>
                {pair.polymarket?.side && (
                  <Chip
                    label={pair.polymarket.side.toUpperCase()}
                    size="small"
                    color={pair.polymarket.side === 'yes' ? 'success' : 'error'}
                    variant="outlined"
                    sx={{
                      fontSize: '0.42rem',
                      height: 12,
                      '& .MuiChip-label': { px: 0.25 },
                      opacity: 0.7
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <a
                  href={pair.kalshi?.marketUrl ?? liveState?.kalshiMarketUrl ?? undefined}
                  target="_blank"
                  rel="noopener"
                  onClick={e => e.stopPropagation()}
                >
                  <img
                    src="/assets/exchanges/png/100x100/kalshi.png"
                    alt="K"
                    style={{ width: 13, height: 13, borderRadius: '50%' }}
                  />
                </a>
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: 'grey.400',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {pair.kalshiTitle || pair.pairName}
                </Typography>
                {pair.kalshi?.side && (
                  <Chip
                    label={pair.kalshi.side.toUpperCase()}
                    size="small"
                    color={pair.kalshi.side === 'yes' ? 'success' : 'error'}
                    variant="outlined"
                    sx={{
                      fontSize: '0.42rem',
                      height: 12,
                      '& .MuiChip-label': { px: 0.25 },
                      opacity: 0.7
                    }}
                  />
                )}
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Market Data — only the ASK side is decision-relevant for entry. */}
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(pAsk)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(pAskSize)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(kAsk)}</Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{fmtQty(kAskSize)}</Typography>
      </TableCell>

      {/* Parameters */}
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Tooltip title={thresholdError ?? ''} placement="top" disableHoverListener={!thresholdError}>
          <span>
            <TextField
              variant="outlined"
              size="small"
              value={threshold}
              placeholder="0.95"
              onChange={e => setThreshold(e.target.value)}
              onBlur={handleSave}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
              }}
              error={!!thresholdError}
              helperText={thresholdError ?? ''}
              sx={inSx}
              inputProps={{ step: 0.01, min: 0, max: 0.999 }}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <TextField
          variant="outlined"
          size="small"
          value={size}
          placeholder="50"
          onChange={e => setSize(e.target.value)}
          onBlur={handleSave}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSave();
          }}
          sx={inSx}
          inputProps={{ step: 1, min: 1 }}
        />
      </TableCell>
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <TextField
            variant="outlined"
            size="small"
            value={maxVol}
            placeholder="500"
            onChange={e => setMaxVol(e.target.value)}
            onBlur={handleSave}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
            }}
            sx={inSx}
            inputProps={{ step: 100, min: 0 }}
          />
          {saving ? (
            <CircularProgress size={12} sx={{ ml: 0.2 }} />
          ) : isDirty && rule ? (
            <IconButton
              size="small"
              onClick={handleSave}
              sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
            >
              <CheckIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          ) : null}
        </Stack>
      </TableCell>

      {/* Live Sums */}
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 700,
            color: overHardCap ? '#f44336' : meetsThreshold ? '#4caf50' : '#42a5f5'
          }}
        >
          {fmtPrice(grossSum)}
        </Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: delta == null ? 'grey.500' : delta >= 0 ? '#4caf50' : '#f44336'
          }}
        >
          {delta == null ? '—' : (delta >= 0 ? '+' : '') + delta.toFixed(3)}
        </Typography>
      </TableCell>

      {/* Buy toggles */}
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingPoly ? (
          <CircularProgress size={14} />
        ) : (
          <Switch
            size="small"
            checked={rule ? rule.polymarketEnabled !== false : false}
            onChange={(_, v) => handleToggle('polymarket', v)}
          />
        )}
      </TableCell>
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingKalshi ? (
          <CircularProgress size={14} />
        ) : (
          <Switch
            size="small"
            checked={rule ? rule.kalshiEnabled !== false : false}
            onChange={(_, v) => handleToggle('kalshi', v)}
          />
        )}
      </TableCell>
    </TableRow>

    {/* Confirmation dialog with three-layer hard-cap guard. */}
    <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '0.9rem', pb: 0.5 }}>
        Activate {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'} Entry
      </DialogTitle>
      <DialogContent>
        <Box sx={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Pair:</b> {pair.pairName}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Platform:</b>{' '}
            {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'} (entry)
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Threshold sum:</b>{' '}
            {Number.isFinite(thresholdNum) ? thresholdNum.toFixed(3) : '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Order size:</b> {size || DEFAULT_SIZE} contracts
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Max volume:</b> ${maxVol || DEFAULT_MAX_VOL}/day
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>P.Ask now:</b> {fmtPrice(pAsk)} &nbsp;|&nbsp; <b>K.Ask now:</b> {fmtPrice(kAsk)}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            <b>Live sum (asks):</b> {fmtPrice(grossSum)}
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              p: 0.8,
              borderRadius: 1,
              backgroundColor: overHardCap ? 'rgba(244,67,54,0.18)' : 'rgba(76,175,80,0.10)',
              color: overHardCap ? '#f44336' : '#bdbdbd',
              border: overHardCap
                ? '1px solid rgba(244,67,54,0.4)'
                : '1px solid rgba(76,175,80,0.3)'
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
              Hard cap: $1.00. Current sum:{' '}
              {grossSum != null ? `$${grossSum.toFixed(3)}` : '—'}
            </Typography>
            {overHardCap && (
              <Typography sx={{ fontSize: '0.7rem', mt: 0.2 }}>
                Sum is at or above the $1 hard cap — entry is blocked.
              </Typography>
            )}
          </Box>
          {priceDriftedSinceOpen && !overHardCap && (
            <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', mt: 0.5 }}>
              Prices changed by more than one tick since this dialog opened. Click
              Re-confirm to snap to the current prices.
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', mt: 0.5 }}>
            This will start placing real BUY orders on{' '}
            {confirmDialog?.platform === 'kalshi' ? 'Kalshi' : 'Polymarket'}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog(null)} size="small">
          Cancel
        </Button>
        {priceDriftedSinceOpen && !overHardCap ? (
          <Button onClick={handleReconfirm} variant="contained" color="warning" size="small">
            Re-confirm
          </Button>
        ) : (
          <Tooltip
            title={overHardCap ? 'Above $1 hard cap' : ''}
            placement="top"
            disableHoverListener={!overHardCap}
          >
            <span>
              <Button
                onClick={handleConfirmToggle}
                variant="contained"
                color="success"
                size="small"
                disabled={overHardCap}
              >
                Activate
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
}, (prev, next) => (
  prev.pair === next.pair
  && prev.rule === next.rule
  && prev.anyRule === next.anyRule
  && prev.liveState === next.liveState
  && prev.onCreateRule === next.onCreateRule
  && prev.onTogglePlatform === next.onTogglePlatform
  && prev.onUpdateRule === next.onUpdateRule
));
